/**
 * The Monitor's live cameras: one card per camera with its latest picture (refreshed while the
 * agent records), whether it records, when the agent last sent a picture and a segment, and why
 * it failed when it did. A collaborator adds a camera by its stream address, switches it on or
 * off, opens its last recording or removes it. Every segment becomes a case recording that the
 * analysis and the Monitor's rules read like any other.
 *
 * Pure: the cameras, the picture URL and the actions arrive as props.
 */
import { Cctv, ExternalLink, Plus, Trash2, VideoOff } from 'lucide-react'
import { type FormEvent, useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useAuthedImageUrl } from '@/hooks/use-authed-image-url'
import type { CameraCreate, CameraDto } from '@/services/api/case-cameras'
import { formatExactDateTime } from '@/utils/date'
import { type LoadView, MonitorSection } from './MonitorSection'

const COPY = 'cases.monitor.cameras'
/** The segment lengths offered, in seconds. */
const SEGMENTS = [60, 120, 300, 600] as const

export interface CameraActions {
  create: (body: CameraCreate) => Promise<void>
  setEnabled: (cameraId: string, enabled: boolean) => void
  remove: (cameraId: string) => void
  pending: boolean
  /** The last write was refused. */
  failed: boolean
}

export interface CamerasPaneProps {
  view: LoadView<CameraDto>
  snapshotUrl: (cameraId: string, tick: number) => string
  refreshMs: number
  /** Omitted for a reader who may not change the cameras. */
  actions: CameraActions | null
  onOpenDocument: (documentId: string) => void
  onRetry: () => void
}

/** A camera's state: recording and fresh, recording but silent, failing, or switched off. */
function cameraState(camera: CameraDto): 'live' | 'waiting' | 'error' | 'off' {
  if (!camera.enabled) return 'off'
  if (camera.last_error) return 'error'
  const seen = camera.last_seen_at ? Date.parse(camera.last_seen_at) : 0
  return Date.now() - seen < 30_000 ? 'live' : 'waiting'
}

const STATE_BADGE = {
  live: 'success',
  waiting: 'warning',
  error: 'destructive',
  off: 'muted',
} as const

function LivePicture({ url, alt }: { url: string | null; alt: string }) {
  const src = useAuthedImageUrl(url)
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-md bg-muted">
      {src ? (
        <img src={src} alt={alt} className="size-full object-contain" />
      ) : (
        <div className="flex size-full items-center justify-center text-muted-foreground">
          <VideoOff aria-hidden className="size-6" />
        </div>
      )}
    </div>
  )
}

function useTick(refreshMs: number, on: boolean): number {
  const [tick, setTick] = useState(() => Date.now())
  useEffect(() => {
    if (!on) return
    const timer = window.setInterval(() => setTick(Date.now()), refreshMs)
    return () => window.clearInterval(timer)
  }, [refreshMs, on])
  return tick
}

function CameraCard({
  camera,
  pane,
}: {
  camera: CameraDto
  pane: Omit<CamerasPaneProps, 'view' | 'onRetry'>
}) {
  const { t, i18n } = useTranslation()
  const when = (value: string | null) =>
    value ? formatExactDateTime(value, i18n.language) : t(`${COPY}.never`)
  const state = cameraState(camera)
  const tick = useTick(pane.refreshMs, camera.enabled)
  const url = camera.has_snapshot ? pane.snapshotUrl(camera.id, tick) : null
  return (
    <li
      className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3"
      data-camera={camera.id}
    >
      <LivePicture url={url} alt={t(`${COPY}.pictureAlt`, { name: camera.name })} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium text-foreground">{camera.name}</span>
          <span className="truncate font-mono text-xs text-muted-foreground">
            {camera.stream_host}
          </span>
        </div>
        <Badge variant={STATE_BADGE[state]}>{t(`${COPY}.state.${state}`)}</Badge>
      </div>
      <dl className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
        <dt>{t(`${COPY}.lastPicture`)}</dt>
        <dd className="text-foreground">{when(camera.last_seen_at)}</dd>
        <dt>{t(`${COPY}.lastSegment`)}</dt>
        <dd className="text-foreground">{when(camera.last_segment_at)}</dd>
        <dt>{t(`${COPY}.segment`)}</dt>
        <dd className="text-foreground">{t(`${COPY}.seconds`, { count: camera.segment_s })}</dd>
      </dl>
      {camera.last_error ? (
        <p className="text-xs text-destructive" role="alert">
          {camera.last_error}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        {pane.actions ? (
          <Label className="flex items-center gap-2 font-normal">
            <Switch
              checked={camera.enabled}
              disabled={pane.actions.pending}
              onCheckedChange={on => pane.actions?.setEnabled(camera.id, on)}
            />
            {t(`${COPY}.record`)}
          </Label>
        ) : null}
        <span className="flex-1" />
        {camera.last_document_id ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => pane.onOpenDocument(camera.last_document_id as string)}
          >
            <ExternalLink aria-hidden />
            {t(`${COPY}.openLast`)}
          </Button>
        ) : null}
        {pane.actions ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            aria-label={t(`${COPY}.remove`, { name: camera.name })}
            disabled={pane.actions.pending}
            onClick={() => pane.actions?.remove(camera.id)}
          >
            <Trash2 aria-hidden />
          </Button>
        ) : null}
      </div>
    </li>
  )
}

function AddCamera({ actions }: { actions: CameraActions }) {
  const { t } = useTranslation()
  const id = useId()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [segment, setSegment] = useState<number>(120)
  const ready = name.trim() !== '' && /^(rtsps?|https?):\/\//.test(url.trim()) && !actions.pending
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!ready) return
    try {
      await actions.create({
        name: name.trim(),
        stream_url: url.trim(),
        segment_s: segment,
        enabled: true,
      })
      setOpen(false)
      setName('')
      setUrl('')
    } catch {
      // The pane says the write was refused; the dialog keeps what was typed.
    }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" size="sm">
          <Plus aria-hidden />
          {t(`${COPY}.add`)}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t(`${COPY}.addTitle`)}</DialogTitle>
          <DialogDescription>{t(`${COPY}.addDescription`)}</DialogDescription>
        </DialogHeader>
        <form onSubmit={event => void submit(event)} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-name`}>{t(`${COPY}.name`)}</Label>
            <Input
              id={`${id}-name`}
              value={name}
              maxLength={200}
              onChange={e => setName(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-url`}>{t(`${COPY}.stream`)}</Label>
            <Input
              id={`${id}-url`}
              value={url}
              autoComplete="off"
              placeholder="rtsps://…"
              onChange={e => setUrl(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t(`${COPY}.streamHint`)}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label id={`${id}-segment`}>{t(`${COPY}.segment`)}</Label>
            <Select value={String(segment)} onValueChange={value => setSegment(Number(value))}>
              <SelectTrigger aria-labelledby={`${id}-segment`} className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEGMENTS.map(seconds => (
                  <SelectItem key={seconds} value={String(seconds)}>
                    {t(`${COPY}.seconds`, { count: seconds })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t(`${COPY}.cancel`)}
            </Button>
            <Button type="submit" disabled={!ready}>
              {t(`${COPY}.save`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function CamerasPane(props: CamerasPaneProps) {
  const { t } = useTranslation()
  const id = useId()
  const { view, onRetry, actions } = props
  return (
    <div className="flex flex-col gap-3">
      <MonitorSection<CameraDto>
        heading={{ id, icon: Cctv, title: t(`${COPY}.title`) }}
        action={actions ? <AddCamera actions={actions} /> : undefined}
        view={view}
        copy={{
          loading: t(`${COPY}.loading`),
          failed: t(`${COPY}.failed`),
          empty: t(`${COPY}.empty`),
        }}
        onRetry={onRetry}
      >
        {rows => (
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {rows.map(camera => (
              <CameraCard key={camera.id} camera={camera} pane={props} />
            ))}
          </ul>
        )}
      </MonitorSection>
      {actions?.failed ? (
        <p className="text-xs text-destructive" role="alert">
          {t(`${COPY}.writeFailed`)}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">{t(`${COPY}.agentHint`)}</p>
    </div>
  )
}
